// As peças PROVISÓRIAS do itinerário — as linhas do recibo da bancada enquanto o voucher
// reconcilia, o fecho do painel e o bloco sozinho da tela "Solicitação enviada".
// Separadas do `TripItinerary.tsx` pelo teto de `max-lines`; o itinerário as compõe nos
// dias dele e reexporta `ProvisionalItinerary` pelo caminho público de sempre.
import { HourglassIcon } from '@phosphor-icons/react';
import { AirlineMark } from './airline_logos.js';
import { t } from './i18n.js';
import { ModalIcon } from './primitives.js';
import type { ProvisionalDay, ProvisionalRow } from './provisional.js';
import type { ModalKind } from './derive.js';
import type { BancadaOutcomeKind, BookingReceipt } from './receipt_types.js';
import { receiptModal } from './trip_cost.js';

/** A frase de fecho, uma por desfecho da bancada e por vocabulário. A copy do desfecho
 *  não serve aqui — ela manda "acompanhe em Viagens", e quem lê já está em Viagens —,
 *  mas a régua de vocabulário é a dela (`booking.checkout.outcome.hotel.*`): cada produto
 *  fala de quem confirma e do que aparece (#5514) — a companhia que EMITE os documentos, o
 *  hotel que CONFIRMA o número da reserva, a viação, a locadora, a cooperativa que ACEITA
 *  a corrida. "Reserva garantida" ao lado de "Aguardando aprovação" num hotel se
 *  contradizia; "quando a companhia emitir" num carro, também. */
const PROVISIONAL_NOTE = {
  FLIGHT: {
    reserved: 'trips.provisional.note.reserved',
    in_progress: 'trips.provisional.note.inProgress',
    indeterminate: 'trips.provisional.note.indeterminate',
  },
  HOTEL: {
    reserved: 'trips.provisional.note.hotel.reserved',
    in_progress: 'trips.provisional.note.hotel.inProgress',
    indeterminate: 'trips.provisional.note.hotel.indeterminate',
  },
  BUS: {
    reserved: 'trips.provisional.note.bus.reserved',
    in_progress: 'trips.provisional.note.bus.inProgress',
    indeterminate: 'trips.provisional.note.bus.indeterminate',
  },
  RENTAL: {
    reserved: 'trips.provisional.note.rental.reserved',
    in_progress: 'trips.provisional.note.rental.inProgress',
    indeterminate: 'trips.provisional.note.rental.indeterminate',
  },
  MOBILITY: {
    reserved: 'trips.provisional.note.mobility.reserved',
    in_progress: 'trips.provisional.note.mobility.inProgress',
    indeterminate: 'trips.provisional.note.mobility.indeterminate',
  },
} as const satisfies Record<ModalKind, Record<BancadaOutcomeKind, string>>;

/** O estado de cada linha provisória: a MESMA palavra que a linha real vestirá quando
 *  o voucher chegar — `reserved` já está em aprovação; os degradados, solicitados. */
export function provisionalStatusLabel(receipt: BookingReceipt): string {
  return receipt.outcome === 'reserved'
    ? t('trips.voucherStatus.AWAITING_APPROVAL')
    : t('trips.voucherStatus.REQUESTED');
}

/**
 * O itinerário PROVISÓRIO sozinho — os trechos do recibo da bancada, com os próprios
 * dias. É a tela "Solicitação enviada" (`BancadaSent`, #2116): o painel "Sua viagem" ao
 * lado do desfecho imprime exatamente este provisório. Lá o portão `provisional` do
 * TripPanel não se aplica — o recibo acabou de nascer e não existe serviço vivo com que
 * conflitar. O painel da viagem NÃO usa este bloco: lá as linhas entram nos dias do
 * `TripItinerary`, junto dos passos emitidos.
 *
 * Um `<section>` com nome acessível (landmark `region`, o mesmo par que a banda "A
 * seguir" usa) porque a fronteira entre o que é DOCUMENTO e o que é PROMESSA precisa
 * existir no DOM, e o tracejado da borda não fala.
 */
export function ProvisionalItinerary({
  receipt,
  days,
}: Readonly<{
  receipt: BookingReceipt;
  days: ProvisionalDay[];
}>) {
  const statusLabel = provisionalStatusLabel(receipt);
  return (
    <section className="tp-prov" aria-label={t('trips.provisional.label')}>
      {days.map((day) => (
        <div className="tp-day" key={day.key}>
          <h3 className="tp-day__ttl">{day.heading}</h3>
          {day.rows.map((row, i) => (
            <ProvisionalStop
              key={row.key}
              row={row}
              linked={i < day.rows.length - 1}
              statusLabel={statusLabel}
            />
          ))}
        </div>
      ))}
    </section>
  );
}

/** O fecho do provisório no painel da viagem: quem vai + a frase que diz onde o
 *  documento vai aparecer. Também dentro da fronteira acessível — fala do recibo, não
 *  do itinerário emitido. A tela "Solicitação enviada" não o tem (#2116): o aceite
 *  dela já diz o estado. */
export function ProvisionalClose({ receipt }: Readonly<{ receipt: BookingReceipt }>) {
  const modal = receiptModal(receipt) ?? 'FLIGHT';
  const vocabulary = modal === 'HOTEL' ? 'hotel' : 'flight';
  return (
    <div className="tp-prov__close" role="group" aria-label={t('trips.provisional.label')}>
      {/* Quem vai: no voo o valor da linha é POR ASSENTO e o qualificador diz "por
          passageiro" — sem os nomes, a frase fica sem sujeito. No hotel quem vai é
          hóspede. O rótulo pluraliza pela contagem, como o resto do painel. */}
      <p className="tp-prov__pax">
        <span className="tp-prov__paxk">
          {t(vocabulary === 'hotel' ? 'trips.provisional.guests' : 'trips.provisional.travelers', {
            count: receipt.travelers.length,
          })}
        </span>{' '}
        {receipt.travelers.map((traveler) => traveler.name).join(', ')}
      </p>
      <p className="tp-prov__note">{t(PROVISIONAL_NOTE[modal][receipt.outcome])}</p>
    </div>
  );
}

/**
 * Uma linha provisória. Veste a anatomia da `StopRow` — a mesma calha, o mesmo cartão,
 * as mesmas faixas de texto —, e o que ela não tem é informação, não descuido:
 *
 *   · sem chip de código: não existe `voucherCode` nem localizador antes da emissão, e a
 *     AUSÊNCIA do chip é como este painel diz "sem documento" (`StopRow` só o desenha
 *     quando existe);
 *   · sem a seta: não há voucher para abrir. Nenhum `button` nasce dentro do bloco.
 *
 * O estado é TEXTO em âmbar e nunca selo: o selo é reservado ao CONFIRMADO (ver
 * `StopState`), porque "em emissão" em selo lê como conquista. O rótulo vem de
 * `trips.voucherStatus.*` (`provisionalStatusLabel`) — a mesma chave que a linha real
 * usa.
 */
export function ProvisionalStop({
  row,
  linked,
  statusLabel,
}: Readonly<{ row: ProvisionalRow; linked: boolean; statusLabel: string }>) {
  return (
    <div className={linked ? 'tp-stop tp-stop--prov tp-stop--linked' : 'tp-stop tp-stop--prov'}>
      <span className="tp-stop__rail" aria-hidden="true">
        {/* `row.modal` é opcional (default `'FLIGHT'`): toda linha era de voo até a
            Task 9, e a linha de hotel é a primeira que precisa dizer o próprio ícone. */}
        <ModalIcon modal={row.modal ?? 'FLIGHT'} size={15} />
      </span>
      {/* A MESMA anatomia da `StopRow` embarcada (paridade #2116): logo como thumb
          na calha, hora embaixo, rota em códigos no título, cidades na linha do
          lugar e o número do voo junto do horário. A régua de quem tem marca é da
          DERIVAÇÃO (`provisional.ts`): `row.carrier` só vem preenchido quando
          existe logo curada — e aí `row.flightNo` é só o número; sem logo, a
          etiqueta da companhia já está dentro do `row.flightNo`. `row.name`
          ("Voo de ida") segue como rótulo acessível do card — o visual não o
          imprime mais. */}
      <div className="tp-stop__card" aria-label={row.name}>
        <span className="tp-stop__who">
          {row.carrier ? (
            <AirlineMark carrier={row.carrier} className="tp-stop__logo" size={28} />
          ) : (
            <span className="tp-stop__thumb" aria-hidden="true">
              <ModalIcon modal={row.modal ?? 'FLIGHT'} size={20} />
            </span>
          )}
          <span aria-hidden="true" />
          <span className="tp-stop__time">{row.time}</span>
        </span>
        <div className="tp-stop__main">
          <div className="tp-prov__line">
            <span className="tp-stop__ttl">{row.detail}</span>
            {row.price && <span className="tp-prov__price">{row.price}</span>}
          </div>
          <div className="tp-prov__line">
            <span className="tp-stop__sub">{row.place ?? row.name}</span>
            <span className="tp-stop__st">
              <HourglassIcon size={11} aria-hidden />
              {statusLabel}
            </span>
          </div>
          {/* A terceira faixa só nasce com conteúdo: nos modais terrestres a linha
              emitida não tem rodapé, e a provisória não ganha uma faixa vazia. */}
          {(row.timeline ?? row.qualifier) && (
            <div className="tp-prov__line">
              <span className="tp-stop__sub">
                {[row.timeline, row.flightNo].filter(Boolean).join(' · ')}
              </span>
              {row.qualifier && <span className="tp-prov__qual">{row.qualifier}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
